@let val = value();
@let selectedOpts = selectedOptions();
@let isMultiple = multiple();
@let isDisabled = disabled();
@let placeholderTxt = placeholder();
@let placeholderCls = placeholderClass;
@let chipOf = overflow();
@let loading = isLoading();
@let hasError = error();

<div
  #select
  ngpSelect
  [ngpSelectValue]="val"
  [ngpSelectDisabled]="isDisabled"
  [ngpSelectCompareWith]="compareWith()"
  [ngpSelectMultiple]="isMultiple"
  [class]="selectClass()"
  (ngpSelectValueChange)="onValueChange($event)"
  (ngpSelectOpenChange)="onOpenChange($event)"
  (blur)="onBlur()"
  ngpSelectDropdownOffset="8"
>
  @if (isMultiple) {
    <div #chipContainer class="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
      <div aria-hidden="true" class="pointer-events-none invisible absolute flex gap-1">
        @for (opt of selectedOpts; track optionId(opt)) {
          <span #measureChip [class]="chipClass">
            {{ resolveDisplay(opt) }}
            <span class="size-4 shrink-0"></span>
          </span>
        }
      </div>

      @if (selectedOpts.length === 0) {
        <span [class]="placeholderCls">{{ placeholderTxt | mlTranslate }}</span>
      } @else {
        @for (opt of chipOf.visible; track optionId(opt)) {
          <ml-chip
            mlDismissable
            [label]="resolveDisplay(opt)"
            (click)="$event.stopPropagation()"
            (dismissed)="deselect(opt)"
          />
        }

        @if (chipOf.count > 0) {
          <ml-chip [label]="'+' + chipOf.count" [mlTooltip]="chipOf.tooltip" (click)="$event.stopPropagation()" />
        }
      }
    </div>
  } @else {
    @if (val) {
      @if (valueDirective(); as dir) {
        <ng-template [ngTemplateOutlet]="dir.template" [ngTemplateOutletContext]="{ $implicit: val }" />
      } @else {
        <span class="flex-auto truncate text-start">{{ resolveDisplay(val) }}</span>
      }
    } @else {
      <span [class]="placeholderCls">{{ placeholderTxt | mlTranslate }}</span>
    }
  }

  <div
    *ngpSelectPortal
    ngpSelectDropdown
    data-select-dropdown
    [class]="dropdownClass()"
    [style.width.px]="dropdownWidth()"
    (scroll)="onDropdownScroll($event)"
  >
    @if (search()) {
      <div class="sticky top-0 z-10 w-full bg-white py-2">
        <div class="relative">
          <ml-icon name="search" class="pointer-events-none absolute inset-y-0 left-1 z-10 flex items-center" />
          <input
            type="search"
            [placeholder]="'select.searchPlaceholder' | mlTranslate"
            [attr.aria-label]="'select.searchPlaceholder' | mlTranslate"
            class="pl-8"
            [class]="optionClass"
            (click)="$event.stopPropagation()"
            (input)="onSearch($event)"
          />
        </div>
        <div class="h-px w-full rounded-full bg-neutral-300"></div>
      </div>
    }
    @for (group of groupedOptions(); track group.title ?? 'default') {
      @if (group.title) {
        <div class="flex items-center gap-1 px-3 py-1 text-xs font-semibold text-neutral-500 uppercase">
          {{ group.title | mlTranslate }}
          <div class="h-px w-full rounded-full bg-neutral-300"></div>
        </div>
      }

      @for (opt of group.items; track optionId(opt)) {
        <div [class]="optionWrapperClass">
          <div
            ngpSelectOption
            [class]="optionClass"
            [ngpSelectOptionValue]="opt"
            [ngpSelectOptionDisabled]="resolveDisabled(opt)"
            (ngpSelectOptionActivated)="onOptionActivated(opt)"
          >
            <div class="flex w-full justify-between">
              @if (optionDirective(); as dir) {
                <ng-template [ngTemplateOutlet]="dir.template" [ngTemplateOutletContext]="{ $implicit: opt }" />
              } @else {
                {{ resolveDisplay(opt) }}
              }

              <span class="flex size-4 shrink-0 items-center justify-center">
                @if (isSelected(opt)) {
                  <ml-icon name="check" />
                }
              </span>
            </div>
          </div>
        </div>
      } @empty {
        @if (!loading && !hasError) {
          <div [class]="emptyClass">{{ emptyText() | mlTranslate }}</div>
        }
      }
    }
    @if (loading) {
      <div class="p-2 text-center text-sm text-neutral-500">{{ 'select.loading' | mlTranslate }}</div>
    } @else if (hasError) {
      <div [class]="emptyClass">{{ errorText() | mlTranslate }}</div>
    }
  </div>
  <div class="relative flex h-full items-center">
    @if (showClearBtn() && !required() && val && !isMultiple && !isDisabled) {
      <ml-icon (click)="onValueChange(null)" name="close" />
    } @else {
      <ml-icon [name]="isOpen() ? 'chevronUp' : 'chevronDown'" />
    }
  </div>
</div>
